import _                                               from 'underscore';
import React, { FC, useContext }                       from 'react';
import { Moment }                                      from 'moment';
import { Divider, Header, Icon, List, Popup, Segment } from 'semantic-ui-react';

import { Align, CorvalProject, CorvalProjectHire, SemanticUIColor, Size } from '../../../../../types';
import { TimeService }                                   from '../../../../../services';
import { getFieldWithProjectIdAccessor, sumByAttribute } from '../../../../../util';
import { ProjectQueries }                                from '../../../../../queries/ProjectQueries';
import { ItemFromQuery }                                                  from '../../../../components/from-query/ItemFromQuery';
import { FinePrintMessage }                                               from '../../../../components/util/messaging/FinePrintMessage';
import { TextLink }                                                       from '../../../../components/display/TextLink';
import { StyledContainer }                                                from '../../../../containers';
import { InlineLoadingIndicatorLabel }                                    from '../../../../components/util/loading/InlineLoadingIndicatorLabel';
import { VisualizationSummaryTile }                                       from '../../common/visualization/display/VisualizationSummaryTile';
import { ProjectListItemCalendarItemLayout }                              from './ProjectListItemCalendarItemLayout';
import { GridColumnInfoType }                                             from './ProjectListItemCalendar';
import { DashboardProjectListItemContext }                                from './ProjectListItem';
import { Cell, Grid }                                                     from "styled-css-grid";
import { ActivityCriticalityLabel }                                       from '../../../../components/activities/ActivityCriticalityLabel';
import { ACTIVITY_CRITICALITY, ENTITY_COLORIZATION, ENTITY_ICONS }        from '../../../../../constants/ProductEnums';
import { BusinessFunctionDot }                                            from '../../../../components/product/BusinessFunctionDot';
import { useFeatureFlags }                                                from '../../../../../hooks';
import { queriesHaveData }                                                from '../../../../../store/_apolloUtil';
import { RoleAccessControl }                                              from '../../../../components/util/access/RoleAccessControl';

type ItemSetByDate = [ startDate: Moment, hireSet: CorvalProjectHire[] ];

interface Props {
    popupItemCount?: number,
    gridColumnCount: number,
    gridColumns: GridColumnInfoType[],
}

const defaultProps: Props = {
    popupItemCount:  5,
    gridColumnCount: 0,
    gridColumns:     [],
}

const ProjectListItemHiringCalendar: FC<Props> = ( props ) => {

    let { gridColumns, popupItemCount }            = props,
        { project, operations, updateProjectStat } = useContext( DashboardProjectListItemContext ),
        featureFlags                               = useFeatureFlags();

    let showHiringDesc = featureFlags.displayHiringTypeDescriptions;

    if( !gridColumns || _.isEmpty( gridColumns ) ) return null;

    let [ startTime ] = _.first( gridColumns ),
        [ , endTime ] = _.last( gridColumns );

    return (

            <ItemFromQuery
                    noItemValid={ true }
                    query={ ProjectQueries.queries.GET_PROJECT_HIRES( `
				id
				hires(
					where: {
						and:[
							{ isActive:{ eq: true } },
							{ hireDate:{ gte: "${ TimeService.momentToISO( startTime ) }" } },
							{ hireDate:{ lte: "${ TimeService.momentToISO( endTime ) }" } }
						]
					}
				){
				    id
				    salary
				    modifier
				    headcount
				    hireDate
				    isActive
				    hireType {
				        id
				        ${ getFieldWithProjectIdAccessor( project.id, 'name' ) }
				        ${ getFieldWithProjectIdAccessor( project.id, 'description' ) }
				        businessFunction
				    }
				  }
			` ) }
                    queryOptions={ {
                        variables: {
                            id: project.id,
                        },
                    } }
                    queryResultObject={ ( data: { projects: CorvalProject[] } ) => {

                        if( !data ) return [];

                        return _.chain( _.first( data.projects ).hires ?? [] )
                                .filter( item => item.isActive )
                                .groupBy( item => TimeService.ISOtoDisplayDate( item.hireDate, true ) )
                                .pairs()
                                .sortBy( ( [ startDate, ] ) => startDate )
                                .map( ( [ startDate, itemSet ] ) => ( [ TimeService.ISOtoMoment( startDate ), itemSet ] as ItemSetByDate ) )
                                .value();
                    } }
                    onQueryComplete={ ( request, resultingDataObject ) => {

                        if( queriesHaveData( request ) ){

                            updateProjectStat( { type: 'hiringCount', value: sumByAttribute( _.first( request.data.projects ).hires, 'headcount' ) } );
                        }

                    } }
                    loadingComponent={ () => {

                        return (
                                <Segment color={ SemanticUIColor.PURPLE } textAlign={ 'center' } basic>

                                    <InlineLoadingIndicatorLabel label={ 'Retrieving Hiring Schedule...' }/>

                                </Segment>
                        )
                    } }>

                { ( activitySetsByDate: ItemSetByDate[] ) => (

                        <ProjectListItemCalendarItemLayout
                                items={ activitySetsByDate }
                                itemRender={ ( [ timeMarker, hireSet ] ) => (

                                        <Popup hoverable
                                               wide={ true }
                                               position={ 'top center' }
                                               trigger={

                                                   <StyledContainer className={ 'cursor--pointer' } onClick={ operations.viewHiring.callback }>

                                                       <VisualizationSummaryTile
                                                               bgColor={ SemanticUIColor.PURPLE }
                                                               metricValue={ sumByAttribute( hireSet, 'headcount' ) }
                                                               labelValue={ timeMarker.format( 'M/D' ) }
                                                               className={ 'cursor--pointer' }
                                                               gridProps={ { columnGap: '0px' } }
                                                       />

                                                   </StyledContainer>
                                               }>

                                            <Popup.Content className={ 'text-left' }>

                                                <Header as={ 'h5' } className={ 'no-margin-bottom' }>Scheduled Hiring</Header>
                                                <Header.Subheader className={ 'text--colored-info' }>Starting { timeMarker.format( 'MMM. Do YYYY [(Q]Q[)]' ) }</Header.Subheader>

                                                <Divider/>

                                                <List size={ 'small' } relaxed divided>

                                                    { _.first( hireSet, popupItemCount ).map( ( item, index ) => {

                                                        let { id, hireType, headcount } = item;

                                                        return (
                                                                <List.Item key={ `${ id }-${ index }` }>
                                                                    <List.Icon name={ ENTITY_ICONS.BUDGET.ENTITY_HIRING } color={ 'purple' }/>
                                                                    <List.Content>
                                                                        <Grid columns={ `auto 1fr auto` } rowGap={ '0px' }>
                                                                            <Cell>{ headcount } x</Cell>
                                                                            <Cell>{ hireType.name }</Cell>
                                                                            <Cell>
                                                                                <BusinessFunctionDot businessFunction={ hireType.businessFunction }/>
                                                                            </Cell>

                                                                            { showHiringDesc && (
                                                                                    <Cell left={ 2 } width={ 2 }>
                                                                                        <FinePrintMessage>{ hireType.description }</FinePrintMessage>
                                                                                    </Cell>
                                                                            ) }
                                                                        </Grid>
                                                                    </List.Content>
                                                                </List.Item>
                                                        )
                                                    } ) }

                                                    { popupItemCount && ( hireSet.length > popupItemCount ) && (
                                                            <List.Item>
                                                                <StyledContainer textAlign={ Align.CENTER } padding={ [ Size.SM, null ] }>
                                                                    <TextLink onClick={ operations.viewHiring.callback }>View All Hiring</TextLink>
                                                                </StyledContainer>
                                                            </List.Item>
                                                    ) }

                                                </List>

                                            </Popup.Content>

                                        </Popup>

                                ) }
                                itemDisplayOffsetPX={ -10 }
                                itemMinWidth={ 55 }
                                getItemDate={ ( [ timeMarker, ] ) => timeMarker }
                                containerColor={ SemanticUIColor.PURPLE }
                                containerProps={ { style: { padding: '0 40px 0 0' } } }
                                gridColumnCount={ props.gridColumnCount }
                                gridColumns={ props.gridColumns }
                                emptyComponent={ ( gridColumnInfo ) => (

                                        <StyledContainer as={ 'p' } padding={ [ Size.SM, null ] } textAlign={ Align.CENTER }>

                                            <FinePrintMessage as={ 'span' }>No scheduled hires. <RoleAccessControl featureFlag={ 'permissionProjectResourcePlanningView' }><TextLink onClick={ operations.viewHiring.callback }>{ operations.viewHiring.label }</TextLink></RoleAccessControl></FinePrintMessage>

                                        </StyledContainer>
                                ) }
                        />

                ) }
            </ItemFromQuery>
    );
};

ProjectListItemHiringCalendar.defaultProps = defaultProps;

export { ProjectListItemHiringCalendar }
